<template>
  <DialogFull :data="dialogSettings" @close="close">
    <template v-slot:dialog.body>
      <div class="container col-lg-8 col-sm-12 offers-form">
        <h1 class="mb-5">Offer Sections</h1>
        <div v-if="isFetchingCurrentOfferGroups">
          <v-skeleton-loader v-for="i in 10" :key="i" type="list-item" />
        </div>
        <ValidationObserver ref="offerForm" v-else>
          <draggable v-model="offerGroups">
            <OfferGroupsEditMode
              v-for="group in offerGroups"
              :key="group.id"
              :group="group"
              @remove="onRemoveGroup"
            />
          </draggable>
          <AddOfferGroupAction />
        </ValidationObserver>
      </div>
    </template>
    <template v-slot:dialog.action>
      <PrimaryButton @click.native="onSave" :loading="saving">
        Save Changes
      </PrimaryButton>
    </template>
  </DialogFull>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import { createNamespacedHelpers } from "vuex";
import {
  FETCH_CURRENT_OFFER_GROUPS,
  REMOVE_OFFER_GROUP,
  EDIT_OFFER_GROUP
} from "@/store/modules/offers/actions";
import { OFFERS_NAMESPACE } from "@/store/modules/offers";
import { getSortedOfferGroups } from "@/utils/offers";
import { ValidationObserver } from "vee-validate";
import OfferGroupsEditMode from "@/views/offers/components/OfferGroupsEditMode";
import AddOfferGroupAction from "@/views/offers/actions/AddOfferGroupAction";
import draggable from "vuedraggable";
import { forEach, filter, size, map } from "lodash";

const { mapActions, mapState } = createNamespacedHelpers(OFFERS_NAMESPACE);

export default {
  components: {
    DialogFull,
    PrimaryButton,
    ValidationObserver,
    OfferGroupsEditMode,
    AddOfferGroupAction,
    draggable
  },
  data() {
    return {
      saving: false,
      dialogSettings: {
        dialog: true,
        title: "Edit Documents"
      },
      offerGroups: [],
      removedGroupsIds: []
    };
  },
  computed: {
    ...mapState(["currentOfferGroups", "isFetchingCurrentOfferGroups"]),
    currentOfferId() {
      return this.$route.params.id;
    }
  },
  async created() {
    await this.fetchCurrentOfferGroups(this.currentOfferId);
    this.adjustOfferGroups();
  },
  methods: {
    ...mapActions({
      fetchCurrentOfferGroups: FETCH_CURRENT_OFFER_GROUPS,
      removeOfferGroup: REMOVE_OFFER_GROUP,
      editOfferGroup: EDIT_OFFER_GROUP
    }),
    adjustOfferGroups() {
      this.offerGroups = [...getSortedOfferGroups(this.currentOfferGroups)];
    },
    onRemoveGroup(groupIdToRemove) {
      this.removedGroupsIds.push(groupIdToRemove);
      this.offerGroups = filter(
        this.offerGroups,
        ({ id }) => id !== groupIdToRemove
      );
    },
    getGroupsWithChangedOrder() {
      const changedGroups = [];
      forEach(this.offerGroups, (group, index) => {
        const currentOrder = index + 1;
        if (group.displayOrder !== currentOrder) {
          changedGroups.push({
            id: group.id,
            displayOrder: currentOrder
          });
        }
      });
      return changedGroups;
    },
    async editGroups(changedGroups) {
      const requests = map(changedGroups, group =>
        this.editOfferGroup({
          groupId: group.id,
          data: {
            ...group,
            offer: {
              id: this.currentOfferId
            }
          }
        })
      );
      return Promise.all(requests);
    },
    async removeGroups() {
      const requests = map(this.removedGroupsIds, groupId =>
        this.removeOfferGroup({ offerId: this.currentOfferId, groupId })
      );
      await Promise.all(requests);
    },
    async onSave() {
      this.saving = true;
      if (size(this.removedGroupsIds)) {
        await this.removeGroups();
      }
      const changedGroups = this.getGroupsWithChangedOrder();
      if (size(changedGroups)) {
        await this.editGroups(changedGroups);
      }
      this.fetchCurrentOfferGroups(this.currentOfferId);
      this.saving = false;
      this.close();
    },
    close() {
      this.$emit("close");
    },
    getErrorMessages(errors) {
      return this.showValidationErrors ? errors : "";
    }
  },
  watch: {
    currentOfferGroups() {
      this.adjustOfferGroups();
    }
  }
};
</script>

<style lang="scss" scope></style>
